import { FC, useEffect, useState } from 'react';
import {
  Table,
  Switch,
  InputNumber,
  Button,
  Space,
  Alert,
  message,
  Popconfirm,
  Modal,
  Input,
  Tag,
  Tabs
} from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { NAMESPACE } from '@/constants';
import {
  useListIoTemplatesQuery,
  useSaveIoTemplateMutation,
  useResetIoTemplateMutation,
  useCreateIoTemplateMutation,
  useDeleteIoTemplateMutation,
  type IoTemplateColumn,
  type IoPricingSettings
} from '@/api';
import Loader from '@/components/Loader';
import { PricingSettings } from './PricingSettings';

/**
 * Edits which columns appear on an Insertion Order, in what order and at what
 * width.
 *
 * Templates are listed by the names people already use, since the business runs
 * several Managed variants and three Data ones. Widths are relative: each column
 * takes its share of the table, so a layout cannot overflow the page however
 * many columns are switched on — unlike PandaDoc, where a fixed-width table
 * forces text to wrap once enough columns are visible.
 */
export const LayoutEditor: FC = () => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const { data, isFetching } = useListIoTemplatesQuery();
  const [saveTemplate, { isLoading: isSaving }] = useSaveIoTemplateMutation();
  const [resetTemplate, { isLoading: isResetting }] = useResetIoTemplateMutation();
  const [createTemplate, { isLoading: isCreating }] = useCreateIoTemplateMutation();
  const [deleteTemplate] = useDeleteIoTemplateMutation();
  const [isNewOpen, setIsNewOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const [slug, setSlug] = useState<string | undefined>();
  const [columns, setColumns] = useState<IoTemplateColumn[]>([]);
  const [pricing, setPricing] = useState<IoPricingSettings | null>(null);
  const [dirty, setDirty] = useState(false);

  const template = data?.find((item) => item.slug === slug);

  // No template is chosen on arrival: the list is the landing view, so a user
  // sees every template that exists before editing one.

  // Re-seed the working copy whenever the selected layout changes, so switching
  // kinds never carries unsaved edits across.
  useEffect(() => {
    setColumns(template?.columns ? template.columns.map((c) => ({ ...c })) : []);
    // Templates stored before pricing existed have no settings, so fall back to
    // the defaults rather than rendering an empty tab.
    setPricing(
      template
        ? {
            currency: 'GBP',
            discount: { value: 0, type: 'percent' },
            taxFirst: { value: 0, type: 'percent' },
            taxSecond: { value: 0, type: 'percent' },
            allowLineDiscount: true,
            showDiscountRow: true,
            showTaxRow: false,
            ...template.pricing
          }
        : null
    );
    setDirty(false);
  }, [template]);

  const update = (index: number, patch: Partial<IoTemplateColumn>) => {
    setColumns((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
    setDirty(true);
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= columns.length) return;
    setColumns((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      await saveTemplate({ slug: slug!, columns, pricing: pricing ?? undefined }).unwrap();
      message.success(t('layoutSaved'));
      setDirty(false);
    } catch {
      message.error(t('layoutSaveFailed'));
    }
  };

  const handleReset = async () => {
    try {
      await resetTemplate(slug!).unwrap();
      message.success(t('layoutReset'));
      setDirty(false);
    } catch {
      message.error(t('layoutSaveFailed'));
    }
  };

  if (isFetching && !data) return <Loader fullscreen={false} />;

  const visibleCount = columns.filter((c) => c.visible).length;

  const newTemplateModal = (
    <Modal
        open={isNewOpen}
        title={t('layoutNewTitle')}
        okText={t('create', { ns: NAMESPACE.COMMON })}
        cancelText={t('cancel', { ns: NAMESPACE.COMMON })}
        confirmLoading={isCreating}
        onCancel={() => setIsNewOpen(false)}
        onOk={async () => {
          try {
            // Start from the layout on screen: most new templates are a variant
            // of one that already works.
            const created = await createTemplate({ name: newName, copyFrom: slug }).unwrap();
            setSlug(created.slug);
            setIsNewOpen(false);
            message.success(t('layoutCreated'));
          } catch {
            message.error(t('layoutCreateFailed'));
          }
        }}
        destroyOnHidden
      >
        <Input
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          placeholder={t('layoutNewPlaceholder')}
          aria-label={t('layoutNewTitle')}
          autoFocus
        />
    </Modal>
  );

  // The list is the landing view; picking a template opens its settings.
  if (!slug) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setNewName('');
              setIsNewOpen(true);
            }}
          >
            {t('layoutNewAction')}
          </Button>
        </Space>

        <Table
          rowKey="slug"
          dataSource={data ?? []}
          pagination={false}
          size="middle"
          onRow={(record) => ({ onClick: () => setSlug(record.slug), style: { cursor: 'pointer' } })}
          columns={[
            {
              title: t('layoutColumn'),
              key: 'name',
              render: (_, record) => (
                <Space>
                  <strong>{record.name}</strong>
                  {record.isDefault && <Tag color="blue">{t('layoutDefaultTag')}</Tag>}
                </Space>
              )
            },
            {
              title: t('layoutEnabledColumn'),
              key: 'enabled',
              width: 140,
              render: (_, record) => (
                // Stop the click reaching the row, which would open the editor.
                <span onClick={(event) => event.stopPropagation()}>
                  <Switch
                    size="small"
                    checked={record.enabled !== false}
                    // The default is what Raise IO opens on, so it cannot be hidden.
                    disabled={record.isDefault}
                    aria-label={t('layoutEnabledColumn')}
                    onChange={async (enabled) => {
                      try {
                        await saveTemplate({ slug: record.slug, enabled }).unwrap();
                      } catch {
                        message.error(t('layoutSaveFailed'));
                      }
                    }}
                  />
                </span>
              )
            },
            {
              title: t('layoutColumnsHeading'),
              key: 'columns',
              width: 140,
              render: (_, record) =>
                `${record.columns.filter((column) => column.visible).length} / ${record.columns.length}`
            },
            {
              title: t('pricingCurrency'),
              key: 'currency',
              width: 110,
              render: (_, record) => record.pricing?.currency ?? 'GBP'
            },
            {
              title: t('pricingDiscount'),
              key: 'discount',
              width: 140,
              render: (_, record) => {
                const rate = record.pricing?.discount;
                if (!rate?.value) return <span style={{ opacity: 0.5 }}>—</span>;
                return rate.type === 'percent' ? `${rate.value}%` : `${rate.value}`;
              }
            },
            {
              title: t('actions', { ns: NAMESPACE.COMMON }),
              key: 'actions',
              width: 200,
              render: (_, record) => (
                <Space size={0}>
                  <Button type="link">{t('edit', { ns: NAMESPACE.COMMON })}</Button>
                  {!record.isDefault && (
                    <Button
                      type="link"
                      onClick={async (event) => {
                        event.stopPropagation();
                        try {
                          await saveTemplate({ slug: record.slug, isDefault: true }).unwrap();
                          message.success(t('layoutDefaultSet', { name: record.name }));
                        } catch {
                          message.error(t('layoutSaveFailed'));
                        }
                      }}
                    >
                      {t('layoutSetDefaultAction')}
                    </Button>
                  )}
                </Space>
              )
            }
          ]}
        />

        {newTemplateModal}
      </Space>
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <Button onClick={() => setSlug(undefined)}>{t('layoutBackToList')}</Button>
        <strong style={{ fontSize: 16 }}>{template?.name}</strong>
        <Button type="primary" onClick={handleSave} loading={isSaving} disabled={!dirty || !slug}>
          {t('save', { ns: NAMESPACE.COMMON })}
        </Button>
        <Popconfirm title={t('layoutResetConfirm')} onConfirm={handleReset}>
          <Button loading={isResetting}>{t('layoutResetAction')}</Button>
        </Popconfirm>
        {template && !template.pandaDocTemplateId && !template.isDefault && (
          <Popconfirm
            title={t('layoutDeleteConfirm', { name: template.name })}
            okButtonProps={{ danger: true }}
            onConfirm={async () => {
              await deleteTemplate(template.slug).unwrap();
              setSlug(undefined);
              message.success(t('layoutDeleted'));
            }}
          >
            <Button danger>{t('layoutDeleteAction')}</Button>
          </Popconfirm>
        )}
      </Space>

      <Alert
        type="info"
        showIcon
        message={t('layoutColumnCount', { visible: visibleCount, total: columns.length })}
      />

      <Tabs
        defaultActiveKey="columns"
        items={[
          {
            key: 'columns',
            label: t('layoutColumnsHeading'),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert
                  type="info"
                  showIcon
                  message={t('layoutColumnCount', { visible: visibleCount, total: columns.length })}
                />
                <Table<IoTemplateColumn>
                  rowKey="field"
                  dataSource={columns}
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: t('layoutColumn'),
                      dataIndex: 'label',
                      key: 'label'
                    },
                    {
                      title: t('layoutVisible'),
                      key: 'visible',
                      width: 110,
                      render: (_, record, index) => (
                        <Switch
                          checked={record.visible}
                          onChange={(checked) => update(index, { visible: checked })}
                          // The document needs at least one column to render.
                          disabled={record.visible && visibleCount === 1}
                        />
                      )
                    },
                    {
                      title: t('layoutWidth'),
                      key: 'width',
                      width: 130,
                      render: (_, record, index) => (
                        <InputNumber
                          min={0.2}
                          max={6}
                          step={0.1}
                          value={record.width}
                          onChange={(value) => update(index, { width: Number(value) || 1 })}
                        />
                      )
                    },
                    {
                      title: t('layoutOrder'),
                      key: 'order',
                      width: 110,
                      render: (_, __, index) => (
                        <Space size={4}>
                          <Button
                            size="small"
                            type="text"
                            aria-label={t('layoutMoveUp')}
                            icon={<ArrowUpOutlined />}
                            disabled={index === 0}
                            onClick={() => move(index, -1)}
                          />
                          <Button
                            size="small"
                            type="text"
                            aria-label={t('layoutMoveDown')}
                            icon={<ArrowDownOutlined />}
                            disabled={index === columns.length - 1}
                            onClick={() => move(index, 1)}
                          />
                        </Space>
                      )
                    }
                  ]}
      />
              </Space>
            )
          },
          {
            key: 'pricing',
            label: t('pricingTitle'),
            children: pricing ? (
              <PricingSettings
                value={pricing}
                onChange={(next) => {
                  setPricing(next);
                  setDirty(true);
                }}
              />
            ) : null
          }
        ]}
      />


      {newTemplateModal}
    </Space>
  );
};

export default LayoutEditor;
